/**
 * ISINsSectionWrapper Component
 *
 * Wrapper for ISINsSection to work with the dynamic form system.
 * Provides all the ISIN management logic (add, remove, change, blur).
 *
 * Validation (PR: feature-isin-add-validation-summary)
 * -------------------------------------------------------
 * Clicking "+ ISIN" now runs a multi-field preflight check instead of a
 * single-field toast.  Three layers:
 *
 *  1. Sticky <Alert variant="danger"> at the top of this section listing
 *     every missing required field by name.  The alert has no close button
 *     and disappears automatically once all fields are filled.
 *
 *  2. Per-field error state is injected into formContext so that
 *     DynamicField components in the same tab pick up the red-border /
 *     warning-icon treatment via getFieldErrorStyle / renderWarningMessage.
 *
 *  3. The Alert element is scrolled into view so the user sees it
 *     immediately without having to hunt.
 *
 * As the user fills each field and the value is saved via useSaveOnBlur the
 * underlying caseData in Redux is updated.  A useEffect watching caseData
 * re-runs the validation and removes resolved fields from the list; the
 * Alert drops its count and vanishes when the list empties.
 */
import React from 'react';
import { Alert, Row, Button, Spinner } from 'react-bootstrap';
import { FaPlus } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { ISINEntry, updateLocalIsinEntry } from '../../../../store/slices/caseIsinsSlice';
import { setValidationErrors, clearValidationError } from '../../../../store/slices/caseSetupSlice';
import { KanbanContainer, StyledCardHeader, StyledCardBody } from '../../../styled/CommonStyled';
import ISINsSection from '../ISINsSection';
import SkeletonLoading from '../../../common/SkeletonLoader';
import { useSaveOnBlur } from '../../../../hooks/useSaveOnBlur';
import { generateUUID } from '../../../../lib/generateUUID';
import toast from 'react-hot-toast';
import { useCaseStatus } from '../../../../hooks/useCaseStatus';
import { useCaseSetup } from '../../../../hooks/useCaseSetup';
import type { CustomSectionProps } from '../../../../types/dynamicForm';
import { isFieldEmpty } from '../../../../utils/isFieldEmpty';

// ---------------------------------------------------------------------------
// Required-field descriptor
// ---------------------------------------------------------------------------
interface RequiredFieldDescriptor {
    /** Key matching FieldConfig.key — used for formContext.setFieldError */
    key: string;
    /** Human-readable label shown in the alert */
    alias: string;
    /** Extracts the current value from caseData */
    getValue: (caseData: any) => unknown;
    /**
     * Optional gate — when present the field is only validated if this
     * returns true. When the condition flips to false the field is
     * automatically removed from missingFields in real-time.
     */
    condition?: (caseData: any) => boolean;
}

const REQUIRED_FIELDS: RequiredFieldDescriptor[] = [
    // ── Basic Product Info ────────────────────────────────────────────────
    {
        key: 'investtypeid',
        alias: 'Investment Type',
        getValue: (d) => d?.investmenttype?.id,
    },
    {
        key: 'issuedate',
        alias: 'Issue Date',
        getValue: (d) => d?.issuedate,
    },
    {
        key: 'maturitydate',
        alias: 'Maturity Date',
        getValue: (d) => d?.maturitydate,
    },
    {
        key: 'coponfreqid',
        alias: 'Coupon Frequency',
        getValue: (d) => d?.coponfrequency?.id,
    },
    {
        key: 'copontypeid',
        alias: 'Coupon Type',
        getValue: (d) => d?.copontype?.id,
    },
    // ── Parties Involved ─────────────────────────────────────────────────
    {
        key: 'agenttypeid',
        alias: 'Paying Agent',
        getValue: (d) => d?.agenttypeid,
    },
    {
        key: 'broker',
        alias: 'Broker',
        getValue: (d) => d?.broker,
    },
    {
        key: 'trustee',
        alias: 'Trustee',
        getValue: (d) => d?.trustee,
    },
    // ── Fees ─────────────────────────────────────────────────────────────
    {
        key: 'setupfee',
        alias: 'Setup Fee',
        getValue: (d) => d?.casefee?.setupfee,
    },
    {
        key: 'adminfee',
        alias: 'Admin Fee',
        getValue: (d) => d?.casefee?.adminfee,
    },
    {
        key: 'managementfee',
        alias: 'Management Fee',
        getValue: (d) => d?.casefee?.managementfee,
    },
    {
        key: 'salesfee',
        alias: 'Sales Fee',
        getValue: (d) => d?.casefee?.salesfee,
    },
    {
        key: 'performancefee',
        alias: 'Performance Fee',
        getValue: (d) => d?.casefee?.performancefee,
    },
    {
        key: 'otherfees',
        alias: 'Other Fees',
        getValue: (d) => d?.casefee?.otherfees,
    },
    // ── Associated Costs ─────────────────────────────────────────────────
    {
        key: 'operationalcosts',
        alias: 'Operational Costs',
        getValue: (d) => d?.casecost?.operationalcosts,
    },
    {
        key: 'runningcosts',
        alias: 'Running Costs',
        getValue: (d) => d?.casecost?.runningcosts,
    },
    {
        key: 'payingagentcosts',
        alias: 'Paying Agent Costs',
        getValue: (d) => d?.casecost?.payingagentcosts,
    },
    {
        key: 'auditcosts',
        alias: 'Audit Costs',
        getValue: (d) => d?.casecost?.auditcosts,
    },
    {
        key: 'legalcosts',
        alias: 'Legal Costs',
        getValue: (d) => d?.casecost?.legalcosts,
    },
    // ── Key Dates & Schedules ────────────────────────────────────────────
    {
        key: 'coponpaymentscheduleid',
        alias: 'Coupon Payment Schedule',
        getValue: (d) => d?.coponpaymentscheduleid,
    },
    {
        key: 'coponpaymentdate',
        alias: 'Coupon Payment Date',
        getValue: (d) => d?.coponpaymentdate,
    },
    {
        key: 'earlyredemptiondate',
        alias: 'Early Redemption Date',
        getValue: (d) => d?.earlyredemptiondate,
    },
    // ── Financial & Structural Details ───────────────────────────────────
    {
        key: 'subscriptiondate',
        alias: 'Final Subscription Date',
        getValue: (d) => d?.subscriptiondate,
    },
    {
        key: 'mintradeamt',
        alias: 'Minimum Trade Amount',
        getValue: (d) => d?.mintradeamt,
    },
    {
        key: 'mintradelot',
        alias: 'Minimum Trading Lot',
        getValue: (d) => d?.mintradelot,
    },
    // ── Conditional: Basket Assets (only when Investment Type = Basket) ──
    {
        key: 'basket_assets',
        alias: 'Basket Assets (at least one required)',
        condition: (d) => d?.investmenttype?.typename === 'Basket',
        getValue: (d) => {
            const assets = d?.case_assetbaskets;
            // Filter out pre-created empty/draft rows — BasketAssetsSection inserts
            // a blank row on mount. Only count entries with an actual assetname saved.
            const filledAssets = Array.isArray(assets)
                ? assets.filter((a: any) => !isFieldEmpty(a.assetname))
                : [];
            return filledAssets.length > 0 ? 'filled' : null;
        },
    },
    // ── Conditional: Sales Fee Details (only when Sales Fee paid by Investor = Yes) ──
    {
        key: 'salesnotpaidissuedate',
        alias: 'Sales Fee on Issue Date',
        condition: (d) => d?.casesubscriptiondata?.salesfeepaidbyinves === true,
        getValue: (d) => d?.casesubscriptiondata?.salesnotpaidissuedate,
    },
    {
        key: 'salesnotpaidmaturitydate',
        alias: 'Sales Fee on Maturity Date',
        condition: (d) => d?.casesubscriptiondata?.salesfeepaidbyinves === true,
        getValue: (d) => d?.casesubscriptiondata?.salesnotpaidmaturitydate,
    },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ISINsSectionWrapper: React.FC<CustomSectionProps> = ({ formContext }) => {
    const dispatch = useDispatch();
    const caseData = useSelector((state: RootState) => state.caseSetup.caseData);
    const { canEditIsin, isCaseFreezed } = useCaseStatus();
    const {
        activeCaseId,
        isinEntries,
        isinsLoading,
        createISIN,
        deleteISIN
    } = useCaseSetup();

    const [loadingStates, setLoadingStates] = React.useState<Record<string, boolean>>({});
    const [errorStates, setErrorStates] = React.useState<Record<string, string>>({});
    const [loadingISINState, setLoadingISINState] = React.useState<boolean>(false);

    // ---------------------------------------------------------------------------
    // Validation state
    // ---------------------------------------------------------------------------
    /** Fields currently failing the preflight check: key → alias */
    const [missingFields, setMissingFields] = React.useState<Record<string, string>>({});
    const alertRef = React.useRef<HTMLDivElement>(null);

    /** Compute which required fields are still empty for the given caseData snapshot. */
    const computeMissing = React.useCallback(
        (data: typeof caseData): Record<string, string> => {
            const result: Record<string, string> = {};
            for (const f of REQUIRED_FIELDS) {
                // Skip fields whose condition is not currently met
                if (f.condition && !f.condition(data)) continue;
                if (isFieldEmpty(f.getValue(data))) {
                    result[f.key] = f.alias;
                }
            }
            return result;
        },
        []
    );

    /**
     * When caseData changes (i.e. user saved a field in another section),
     * re-evaluate only the fields that were already flagged as missing.
     * This causes the alert count to drop in real-time as fields are filled.
     * Also handles condition flips (e.g. switching away from Basket clears
     * basket_assets; toggling Sales Fee paid by Investor clears those fields).
     */
    React.useEffect(() => {
        if (Object.keys(missingFields).length === 0) return;
        const stillMissing: Record<string, string> = {};
        for (const key of Object.keys(missingFields)) {
            const descriptor = REQUIRED_FIELDS.find((f) => f.key === key);
            if (descriptor) {
                const conditionMet = !descriptor.condition || descriptor.condition(caseData);
                if (conditionMet && isFieldEmpty(descriptor.getValue(caseData))) {
                    stillMissing[key] = missingFields[key];
                }
            }
        }
        setMissingFields(stillMissing);
        // Clear Redux validation errors for fields that are now filled or no longer applicable
        for (const key of Object.keys(missingFields)) {
            if (!stillMissing[key]) {
                dispatch(clearValidationError(key));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseData]);

    // ---------------------------------------------------------------------------
    // Save-on-blur plumbing
    // ---------------------------------------------------------------------------
    const setFieldLoading = (field: string, isLoading: boolean) => {
        setLoadingStates(prev => ({ ...prev, [field]: isLoading }));
    };

    const setFieldError = (field: string, error: string | null) => {
        setErrorStates(prev => ({ ...prev, [field]: error || '' }));
    };

    const { handleBlur } = useSaveOnBlur({
        activeCaseId,
        setFieldLoading,
        setFieldError
    });

    // ---------------------------------------------------------------------------
    // Add ISIN — multi-field preflight validation
    // ---------------------------------------------------------------------------
    const handleAddISIN = async () => {
        if (!activeCaseId) return;

        const missing = computeMissing(caseData);

        if (Object.keys(missing).length > 0) {
            setMissingFields(missing);
            dispatch(setValidationErrors(missing));
            setTimeout(() => {
                alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 50);
            return;
        }

        // ── Currency uniqueness check — each ISIN must have a unique currency ───────────
        // Only currencies with a saved currencyid are checked; new blank entries
        // don’t yet have a currency and won’t collide.
        const usedCurrencies = new Set(
            isinEntries
                .map((e) => e.currencyid)
                .filter(Boolean)
        );
        if (usedCurrencies.size >= 3) {
            toast.error('Maximum 3 ISINs reached — one per currency (CHF, EUR, USD).');
            return;
        }

        // ── Max 3 ISINs hard cap (belt-and-suspenders — UI also hides the button) ─
        if (isinEntries.length >= 3) {
            toast.error('A compartment may not have more than 3 ISINs.');
            return;
        }

        // All required fields are filled — clear validation errors and proceed.
        setMissingFields({});
        dispatch(setValidationErrors({}));

        setLoadingISINState(true);
        try {
            const newEntry: ISINEntry = {
                id: generateUUID(),
                isinNumber: '',
                valoren: '',
                issueSize: '',
                currencyid: null,
                currency: process.env.REACT_APP_DEFAULT_CURRENCY || 'CHF',
                issuePrice: 0,
                interestRate: 0,
                couponRate: 0,
            };
            await createISIN(newEntry, caseData!.copontype!.id);
        } catch (error) {
            toast.error('Failed to create new ISIN.');
        } finally {
            setLoadingISINState(false);
        }
    };

    const handleRemoveISIN = async (id: string) => {
        if (isinEntries.length <= 1) return;
        try {
            setFieldLoading(id, true);
            await deleteISIN(id);
        } finally {
            setFieldLoading(id, false);
        }
    };

    const renderSkeletonAccordion = () => (
        <div>{[...Array(3)].map((_, i) => <SkeletonLoading key={i} rows={1} height={[40]} />)}</div>
    );

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------
    const missingKeys = Object.keys(missingFields);
    const missingAliases = Object.values(missingFields);

    return (
        <>
            {/* Sticky validation alert — no close button; auto-clears as fields are filled */}
            {missingKeys.length > 0 && (
                <div ref={alertRef}>
                    <Alert variant="danger" className="mb-3">
                        <Alert.Heading as="h6" className="mb-1">
                            {missingKeys.length === 1
                                ? '1 required field must be filled before adding ISINs'
                                : `${missingKeys.length} required fields must be filled before adding ISINs`}
                        </Alert.Heading>
                        {missingAliases.join(', ')}
                    </Alert>
                </div>
            )}

            <Row className="mb-3">
                <KanbanContainer>
                    <StyledCardHeader as="h6" className="d-flex justify-content-between align-items-center">
                        ISINs
                        {isinEntries.length < 3 && (
                            <Button
                                variant="default"
                                size="sm"
                                onClick={handleAddISIN}
                                disabled={!canEditIsin || loadingISINState}
                                style={{ padding: '0.25rem 0.5rem' }}
                            >
                                {loadingISINState ? <Spinner animation="border" size="sm" /> : <FaPlus />}
                            </Button>
                        )}
                    </StyledCardHeader>
                    <StyledCardBody>
                        {isinsLoading ? renderSkeletonAccordion() : (
                            <ISINsSection
                                isinEntries={isinEntries}
                                loadingStates={loadingStates}
                                errorStates={errorStates}
                                onRemoveISIN={handleRemoveISIN}
                                onISINChange={(id, field, value) => {
                                    dispatch(updateLocalIsinEntry({ id, field, value }));
                                }}
                                onISINBlur={(id, field, value) => {
                                    handleBlur(`${field}-${id}`, value);
                                }}
                                disabled={isCaseFreezed}
                            />
                        )}
                    </StyledCardBody>
                </KanbanContainer>
            </Row>
        </>
    );
};

export default ISINsSectionWrapper;
